/**
 * Acrescenta uma linha de comentário à FATURA.
 *
 * Irmão do `AddBillingEventCommentModal`, e a diferença é o alvo: lá o comentário descreve uma AÇÃO
 * de cobrança (um protesto, uma notificação), aqui descreve a fatura em si. As duas coisas são
 * procuradas em momentos diferentes — "o que aconteceu com este protesto?" e "por que esta fatura
 * está assim?".
 *
 * ACRESCENTA, não substitui: o backend acumula o texto e a tela mostra o último com todos no
 * tooltip. A fatura passa por várias mãos ao longo da cobrança, e trocar o texto apagaria o
 * histórico de quem escreveu antes.
 *
 * Vale para fatura em QUALQUER estado, inclusive cancelada e paga — poder escrever numa cobrança
 * encerrada é como se explica depois o que aconteceu com ela.
 */
import { useState } from 'react';
import { FaCommentDots, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { addInvoiceComment } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';

interface Props {
    invoice: any;
    onClose: () => void;
    onAdded: () => void;
    /** para onde o texto vai; sem ele, para os comentários da fatura (`invoice.notes`) */
    submitNote?: (text: string) => Promise<unknown>;
    title?: string;
    zIndexClass?: string;
}

const AddInvoiceCommentModal: React.FC<Props> = ({
    invoice, onClose, onAdded, submitNote, title = 'Adicionar comentário', zIndexClass = 'z-[60]',
}) => {
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setSubmitting(true);
        try {
            await (submitNote
                ? submitNote(text.trim())
                : addInvoiceComment(invoice.invoiceId, text.trim()));
            toast.success('Comentário adicionado.');
            onAdded();
            onClose();
        } catch (e: any) {
            toast.error(e?.message || 'Não foi possível adicionar o comentário');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndexClass} p-4`}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FaCommentDots className="text-blue-500" /> {title}
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                </div>

                <p className="text-xs text-gray-500 mb-3">
                    {invoice.contractLabel && <>{invoice.contractLabel} · </>}
                    {invoice.details?.documentTitle || `fatura ${String(invoice.invoiceId).slice(0, 8)}`}
                    {invoice.amount != null && <> · {formatBRL(invoice.amount)}</>}
                    {' · '}o texto é ACRESCENTADO aos comentários existentes.
                </p>

                <label className="block text-sm font-medium mb-1" htmlFor="comentario-fatura">
                    Comentário
                </label>
                <textarea
                    id="comentario-fatura"
                    className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    rows={3}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="O que está acontecendo com esta fatura"
                />

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    {/* sem texto não há o que acrescentar, e o backend recusaria o POST vazio —
                        melhor o botão dizer isso antes da viagem */}
                    <button onClick={submit} disabled={submitting || !text.trim()}
                        className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                        {submitting ? 'Salvando...' : 'Adicionar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddInvoiceCommentModal;
